/**
 * @typedef {Object} FormFieldEventActionCondition
 * @property {string} equals - The value that the field must equal in order for the action to be triggered
 *
 * @typedef {Object} FormFieldEventAction
 * @property {string} target        - The field that should be affected by the event
 * @property {string} value        - Optional field which, if set, determines the value which the field should be set to. If not set, the value of the triggering field will be used.
 * @property {FormFieldEventActionCondition} condition  - Optional condition object
 *
 * @typedef {Object} FormFieldEvent
 * @property {string} id                - The field whose value has changed
 * @property {FormFieldEventAction[]} setValue  - The fields whose values should be set
 * @property {FormFieldEventAction[]} setVisible    - The fields that should be made visible
 * @property {FormFieldEventAction[]} setHidden    - The fields that should be hidden
 * @property {FormFieldEventAction[]} setRequired   - The fields that should be made required
 * @property {FormFieldEventAction[]} setOptional   - The fields that should be made optional
 *
 * @typedef FormFieldEventActionType {'setValue' | 'setVisible' | 'setHidden' | 'setRequired' | 'setOptional'}
 */

/**
 * Gets all child fields of a repeatable field
 *
 * @param repeatableTemplate
 * @returns {*[]}
 */
const getRepeatableChildren = (repeatableTemplate) => {
    const childFields = [];

    for (const childKey in repeatableTemplate.children) {
        const field = repeatableTemplate.children[childKey];

        childFields.push({
            ...field,
            parent: repeatableTemplate
        });
    }

    return childFields;
}

/**
 * Returns flat array of all fields in a form template, with a parent property on each to to indicate position within the hierarchy
 *
 * @param template
 * @returns {*[]}
 */
const getFields = (template) => {
    let allFields = [];

    for (const headerKey in template.header) {
        allFields.push({
            ...template.header[headerKey],
            parent: null
        });

        for (const subheaderKey in template.header[headerKey].subheader) {
            const subheader = template.header[headerKey].subheader[subheaderKey];

            allFields.push({
                ...subheader,
                parent: template.header[headerKey]
            });

            for (const questionKey in template.header[headerKey].subheader[subheaderKey].question) {
                const field = template.header[headerKey].subheader[subheaderKey].question[questionKey];

                allFields.push({
                    ...field,
                    parent: template.header[headerKey].subheader[subheaderKey]
                });

                if (field.objecttype === 'formRepeatable' || field.objecttype === 'formRow') {
                    allFields = [
                        ...allFields,
                        ...getRepeatableChildren(field)
                    ]
                }
            }
        }
    }

    return allFields;
}

const getFieldById = (targetId, fields) => {
    return fields.find(({id}) => id == targetId);
}

const fieldIsHeader = (field) => {
    return field.objecttype === 'formHeader' || field.objecttype === 'formSubheader';
}

const getNonHeaderFields = (fields) => {
    return fields.filter(({objecttype}) => (objecttype !== 'formHeader') && objecttype !== 'formSubheader');
}

/**
 * Adds an action to an event
 *
 * @param {FormFieldEvent} event             Event object
 * @param action            'setValue'|'setVisible'|'setHidden'|'setRequired'|'setOptional'
 * @param triggerField    ID of the field that triggers the event (i.e. the field whose value has changed)
 * @param targetField     ID of the field that is affected by the event
 * @param condition         Optional condition object
 * @param value             Optional value to set the target field to
 * @returns {*}
 */
const appendEvent = (event, action, triggerField, targetField, condition = {}, value = null) => {
    const valueTo = {};

    if (value) {
        valueTo.value = value;
    }

    event[action].push({
        target: targetField,
        condition,
        ...valueTo
    });

    return event;
}

/**
 * Generates a new event object
 *
 * @param triggerFieldId
 * @returns {FormFieldEvent}
 */
const generateEventObject = (triggerFieldId) => {
    return {
        // The field whose value has changed
        id: triggerFieldId,

        // Trigger the following changes on these fields:
        setValue: [],
        appendAndSetValue: [],
        setVisible: [],
        setHidden: [],
        setRequired: [],
        setOptional: []
    }
}

/**
 * Returns true if the field should trigger any events
 *
 * @param field
 * @returns {boolean}
 */
const fieldHasEvent = (field) => {
    const content = field.content;

    if (content['copyFrom'] && content['copyFrom'] !== '') {
        return true;
    }

    if (content['copyTo'] && content['copyTo'] !== '') {
        return true;
    }

    if (field.objecttype === 'formMultiple' && Object.keys(content.value).length > 0) {
        for (const valueKey in content.value) {
            if (content.value && content.value[valueKey].event) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Returns the existing event from the events array, or generates a new one if one doesn't exist already
 * @param triggerFieldId
 * @param events
 * @returns {{setRequired: *[], setValue: *[], id, setVisibility: *[]}|*}
 */
const getEvent = (triggerFieldId, events) => {
    const existingEvent = events.find(({id}) => id === triggerFieldId);
    return existingEvent ? existingEvent : generateEventObject(triggerFieldId);
}

const fieldIsRepeatable = (field) => {
    return field.objecttype === 'formRepeatable' || field.objecttype === 'formRow' || (field.objecttype === 'formSubheader' && field?.content?.repeatable === true);
}

const fieldHasRepeatableParent = (field) => {
    if (!field.parent) return false;

    return fieldIsRepeatable(field.parent);
}

const fieldsHaveSameParent = (fieldA, fieldB) => {
    return fieldA.parent.id === fieldB.parent.id;
}

/**
 * Builds an array of events from a form template's fields.
 * @param fields
 * @returns {*[]}
 */
const buildEventsArray = (fields) => {
    const events = [];
    const nonHeaderFields = getNonHeaderFields(fields);

    for (const field of nonHeaderFields) {
        let event;
        const content = field.content;
        const hasEvent = fieldHasEvent(field);

        if (hasEvent) {
            if (content['copyFrom'] && content['copyFrom'] !== '') {
                event = getEvent(content['copyFrom'], events);
                event = appendEvent(event, 'setValue', getFieldById(content['copyFrom'], fields), field);

                if (event && !events.find(({id}) => id === event.id)) {
                    events.push(event);
                }
            }

            if (content['copyTo'] && content['copyTo'] !== '') {
                event = getEvent(field.id, events);
                event = appendEvent(event, 'setValue', field, getFieldById(content['copyTo'], fields));

                if (event && !events.find(({id}) => id === event.id)) {
                    events.push(event);
                }
            }

            if (field.objecttype === 'formMultiple' && Object.keys(content.value).length > 0) {
                for (const valueKey in content.value) {
                    const value = content.value[valueKey];

                    if (value.event) {
                        for (const targetFieldId in value.event) {
                            const targetField = nonHeaderFields.find(field => field.id == [targetFieldId]);
                            /**
                             * E.g.
                             * "EAB29DBB-4D63-A23F-A27E-0EF8FB0DD214": {
                                    "visible": 1,
                                    "set_value": "Foo"
                                  },
                             */
                            const eventItem = value.event[targetFieldId];

                            for (const action in eventItem) {
                                let eventAction;
                                let valueTo = null;

                                switch (action) {
                                    case 'visible':
                                        eventAction = eventItem[action] === 1 ? 'setVisible' : 'setHidden';
                                        break;
                                    case 'required':
                                        eventAction = eventItem[action] === 1 ? 'setRequired' : 'setOptional';
                                        break;
                                    case 'set_value':
                                        eventAction = 'setValue';

                                        if (targetField.objecttype === 'formMultiple') {
                                            const v = targetField?.content?.value;
                                            const foundValue = v ? Object.values(v).find(targetOption => targetOption.item == eventItem[action]) : undefined ;

                                            if (foundValue) {
                                                let foundKey;

                                                for (const vKey in v) {
                                                    if (v[vKey]?.item == eventItem[action]) {
                                                        foundKey = vKey;
                                                        break;
                                                    }
                                                }

                                                valueTo = {
                                                    [foundKey]: foundValue.item
                                                };
                                            } else {
                                                eventAction = "appendAndSetValue";

                                                valueTo = {
                                                    "other": {
                                                        "_1": eventItem[action]
                                                    },
                                                    "_1": eventItem[action]
                                                }
                                            }
                                        } else {
                                            valueTo = eventItem[action];
                                        }

                                        break;
                                }

                                event = getEvent(field.id, events);
                                event = appendEvent(event, eventAction, field, getFieldById(targetFieldId, fields), {
                                    equals: value.item,
                                }, valueTo);

                                if (event && !events.find(({id}) => id === event.id)) {
                                    events.push(event);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    return events;
}

const transformValueToTarget = (value, triggerField, targetField) => {
    if (triggerField.objecttype === 'formMultiple' && targetField.objecttype === 'formText') {
        return {
            text: typeof value === 'object' ? Object.values(value)[0] : value
        };
    }

    return value;
}

export default {
    getFields,
    getFieldById,
    fieldHasRepeatableParent,
    fieldIsRepeatable,
    fieldIsHeader,
    fieldsHaveSameParent,
    getRepeatableChildren,
    buildEventsArray,
    getNonHeaderFields,
    transformValueToTarget,
};